import { Workflow, Stethoscope, FileCheck, ClipboardList, Users } from "lucide-react";
import { cn } from "../../lib/utils";
import type { GraphDefinition } from "../../api/graphs";

interface Template {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  definition: GraphDefinition;
}

const TEMPLATES: Template[] = [
  {
    id: "intake",
    name: "Patient Intake Pipeline",
    description: "Automated patient intake with triage routing, data collection, and scheduling",
    icon: ClipboardList,
    color: "text-blue-500 bg-blue-100 dark:bg-blue-900/30",
    definition: {
      nodes: [
        { id: "start", type: "start", position: { x: 300, y: 30 }, data: {} },
        { id: "triage", type: "router", position: { x: 270, y: 140 }, data: { name: "Triage Router" } },
        { id: "intake", type: "agent", position: { x: 80, y: 280 }, data: { name: "Intake Agent", agentType: "worker", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Collect patient demographics and chief complaint." } },
        { id: "schedule", type: "agent", position: { x: 480, y: 280 }, data: { name: "Scheduling Agent", agentType: "worker", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Find available appointment slots." } },
        { id: "ehr", type: "tool", position: { x: 80, y: 420 }, data: { name: "EHR Lookup" } },
        { id: "review", type: "interrupt", position: { x: 280, y: 450 }, data: { name: "Clinical Review" } },
        { id: "end", type: "end", position: { x: 300, y: 570 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "triage" },
        { id: "e2", source: "triage", target: "intake", label: "routine" },
        { id: "e3", source: "triage", target: "schedule", label: "follow-up" },
        { id: "e4", source: "intake", target: "ehr" },
        { id: "e5", source: "ehr", target: "review" },
        { id: "e6", source: "schedule", target: "review" },
        { id: "e7", source: "review", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "prior-auth",
    name: "Prior Authorization",
    description: "Automate prior auth submission, payer communication, and appeals tracking",
    icon: FileCheck,
    color: "text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30",
    definition: {
      nodes: [
        { id: "start", type: "start", position: { x: 300, y: 30 }, data: {} },
        { id: "supervisor", type: "agent", position: { x: 260, y: 140 }, data: { name: "Auth Supervisor", agentType: "supervisor", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Coordinate prior auth process." } },
        { id: "docs", type: "agent", position: { x: 80, y: 280 }, data: { name: "Doc Gatherer", agentType: "worker", modelId: "us.anthropic.claude-3-5-haiku-20241022-v1:0", systemPrompt: "Collect clinical documentation." } },
        { id: "payer", type: "agent", position: { x: 480, y: 280 }, data: { name: "Payer Comms", agentType: "worker", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Handle payer communication." } },
        { id: "approval", type: "interrupt", position: { x: 280, y: 420 }, data: { name: "Staff Approval" } },
        { id: "end", type: "end", position: { x: 300, y: 540 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "supervisor" },
        { id: "e2", source: "supervisor", target: "docs", label: "gather docs" },
        { id: "e3", source: "supervisor", target: "payer", label: "contact payer" },
        { id: "e4", source: "docs", target: "approval" },
        { id: "e5", source: "payer", target: "approval" },
        { id: "e6", source: "approval", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "claims-review",
    name: "Claims Review",
    description: "Automated claim scrubbing, denial analysis, and resubmission workflow",
    icon: Stethoscope,
    color: "text-violet-500 bg-violet-100 dark:bg-violet-900/30",
    definition: {
      nodes: [
        { id: "start", type: "start", position: { x: 300, y: 30 }, data: {} },
        { id: "scrubber", type: "agent", position: { x: 260, y: 140 }, data: { name: "Claim Scrubber", agentType: "worker", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Review claims for errors before submission." } },
        { id: "router", type: "router", position: { x: 260, y: 280 }, data: { name: "Denial Router" } },
        { id: "appeal", type: "agent", position: { x: 80, y: 400 }, data: { name: "Appeal Agent", agentType: "worker", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Draft appeal letters for denied claims." } },
        { id: "resubmit", type: "tool", position: { x: 460, y: 400 }, data: { name: "Resubmit Claim" } },
        { id: "end", type: "end", position: { x: 300, y: 520 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "scrubber" },
        { id: "e2", source: "scrubber", target: "router" },
        { id: "e3", source: "router", target: "appeal", label: "denied" },
        { id: "e4", source: "router", target: "resubmit", label: "fixable" },
        { id: "e5", source: "appeal", target: "end" },
        { id: "e6", source: "resubmit", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "referral",
    name: "Referral Management",
    description: "Track specialist referrals from creation through completion with status updates",
    icon: Users,
    color: "text-amber-500 bg-amber-100 dark:bg-amber-900/30",
    definition: {
      nodes: [
        { id: "start", type: "start", position: { x: 300, y: 30 }, data: {} },
        { id: "coordinator", type: "agent", position: { x: 260, y: 140 }, data: { name: "Referral Coordinator", agentType: "supervisor", modelId: "us.anthropic.claude-sonnet-4-20250514-v1:0", systemPrompt: "Manage referral lifecycle and status tracking." } },
        { id: "notify", type: "tool", position: { x: 80, y: 280 }, data: { name: "Notify Patient" } },
        { id: "schedule", type: "agent", position: { x: 460, y: 280 }, data: { name: "Schedule Specialist", agentType: "worker", modelId: "us.anthropic.claude-3-5-haiku-20241022-v1:0", systemPrompt: "Book specialist appointments." } },
        { id: "followup", type: "interrupt", position: { x: 280, y: 400 }, data: { name: "Staff Follow-up" } },
        { id: "end", type: "end", position: { x: 300, y: 520 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "coordinator" },
        { id: "e2", source: "coordinator", target: "notify", label: "notify" },
        { id: "e3", source: "coordinator", target: "schedule", label: "schedule" },
        { id: "e4", source: "notify", target: "followup" },
        { id: "e5", source: "schedule", target: "followup" },
        { id: "e6", source: "followup", target: "end" },
      ],
      config: {},
    },
  },
];

interface TemplateGalleryProps {
  onSelect: (name: string, definition: GraphDefinition) => void;
  onClose: () => void;
}

export function TemplateGallery({ onSelect, onClose }: TemplateGalleryProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Agent Templates</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Start with a pre-built healthcare workflow</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">
            Cancel
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 p-6">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => onSelect(t.name, t.definition)}
              className="group text-left rounded-lg border border-border p-4 hover:border-primary/40 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", t.color)}>
                  <t.icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">{t.name}</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>
              <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>{t.definition.nodes.length} nodes</span>
                <span>{t.definition.edges.length} edges</span>
              </div>
            </button>
          ))}
          {/* Blank template */}
          <button
            onClick={() =>
              onSelect("Untitled Agent", {
                nodes: [
                  { id: "start", type: "start", position: { x: 250, y: 50 }, data: {} },
                  { id: "end", type: "end", position: { x: 250, y: 400 }, data: {} },
                ],
                edges: [],
                config: {},
              })
            }
            className="group text-left rounded-lg border border-dashed border-border p-4 hover:border-primary/40 transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800">
                <Workflow className="h-4.5 w-4.5" />
              </div>
              <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">Blank Agent</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">Start from scratch with just Start and End nodes</p>
          </button>
        </div>
      </div>
    </div>
  );
}

export { TEMPLATES };
